 

import {
  Component,
  EventEmitter,
  Inject,
  type OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { AccessGuardService, RoleGuardService } from '@odin/odin-authentication';
import {
  Alignment,
  FormBuilder,
  FormData,
  FormOption,
  FormValidation,
  InputIconType,
  InputPrefixSuffixType,
  InputType,
  MerchantChangeService,
  MerchantService,
  MonekColour,
  NotificationService,
  type OdinFeatures,
  OptionInputType,
  Size,
  TestMidCondition,
  ValidationPattern,
} from '@odin/odin-core';
import {
  PEBLFormData,
  PEBLPaymentResponse,
  type PeblScheduleFrequency,
  PeblSendType,
  type ScheduleResponse,
} from '@odin/odin-transactions';
import { PeblConfirmationDialogComponent } from '../pebl-confirmation-dialog/pebl-confirmation-dialog.component';
import { Subscription } from 'rxjs';
import { CountryConvertService } from '@odin/odin-core';
import { CurrencyPipe } from "@angular/common";
import paymentAmountFields from '../payment-amount-fields/payment-amount-fields';

enum PeblFormStage {
  PAYMENT_DETAILS = 0,
  SENDER_METHOD = 1,
  SENDER_INFO = 2,
}

class PeblSendMethod {
  constructor(
    public sendType: PeblSendType,
    public viewName: string,
    public icon: string,
  ) { }
}

@Component({
  standalone: false,
  selector: 'odin-pebl-page',
  templateUrl: './pebl-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-page.component.scss'],
})
export class PeblPageComponent implements OnDestroy {
  public page: 'payment' | 'payment-success' = 'payment';
  public formStage: PeblFormStage = PeblFormStage.PAYMENT_DETAILS;
  public sendMethod: PeblSendType = PeblSendType.NONE;
  public linkResponse: PEBLPaymentResponse | undefined;
  public scheduleResponse: ScheduleResponse | undefined;

  public scheduleEnabled: boolean = false;
  public scheduleMode: 'once' | 'recurring' = 'once';
  public sendAtDate: Date | null = null;
  public sendAtTime: string = '';
  public frequency: PeblScheduleFrequency = 'weekly';
  public startDate: Date | null = null;
  public startTime: string = '';
  public endCondition: 'occurrences' | 'endDate' = 'occurrences';
  public occurrences: number | null = null;
  public endDate: Date | null = null;
  public minScheduleDate: Date = new Date();
  public formSubmit: EventEmitter<void> = new EventEmitter<void>();
  public refreshFormState: EventEmitter<void> = new EventEmitter<void>();

  public peblForm: FormData<PEBLFormData> = this.BuildForm();
  public peblSendForm: FormData<{ sendAddress: string }> = this.BuildSendForm();
  private sendDetails: { sendAddress: string };
  public formsActive: boolean = true;

  public sendMethods: PeblSendMethod[] = [
    new PeblSendMethod(PeblSendType.EMAIL, 'E-mail', 'email'),
    new PeblSendMethod(PeblSendType.LINK, 'Copy Link', 'file_download'),
    new PeblSendMethod(PeblSendType.SMS, 'SMS', 'phone_iphone'),
  ];

  private merchantChangeSub: Subscription;
  public qrEnabled: boolean = false;

  constructor(
    private merchantService: MerchantService,
    private accessService: AccessGuardService,
    private notificationService: NotificationService,
    private merchantChangeService: MerchantChangeService,
    private roleGuardService: RoleGuardService,
    private countryConvertService: CountryConvertService,
    private currencyPipe: CurrencyPipe,
    @Inject('features') private featureHelper: OdinFeatures,
  ) {
    this.qrEnabled = featureHelper['pebl-qr'];
    this.merchantChangeSub =
      this.merchantChangeService.merchantChangeEvent.subscribe(() => {
        this.ResetPage();
        if (!this.PermissionChecker())
          this.accessService.bootNoAccess('Pay By Link');
      });
  }

  ngOnDestroy(): void {
    if (this.merchantChangeSub !== undefined)
      this.merchantChangeSub.unsubscribe();
  }

  public PermissionChecker(): boolean {
    return (
      this.accessService.buidHasFeature('pbl') &&
      (
        this.accessService.hasNewGrant('txn:CreatePebl') ||
        this.roleGuardService.hasPermission('pbl:Create')
      )
    );
  }

  private SubmitForm(data: PEBLFormData): void {
    // update model
    data.method = this.sendMethod;

    if (data.method == PeblSendType.SMS)
      data.phoneNumber = this.sendDetails.sendAddress;
    else if (data.method == PeblSendType.EMAIL)
      data.emailAddress = this.sendDetails.sendAddress;

    if (this.scheduleEnabled) {
      const errors = this.scheduleErrors();
      if (errors.length > 0) {
        this.notificationService.SmallDialog(errors[0], 'Close', 6000);
        return;
      }

      data.scheduleEnabled = true;
      data.scheduleMode = this.scheduleMode;
      if (this.scheduleMode === 'once') {
        data.sendAt = this.combineDateTime(this.sendAtDate, this.sendAtTime);
      } else {
        data.frequency = this.frequency;
        data.startDate = this.combineDateTime(this.startDate, this.startTime);
        data.endCondition = this.endCondition;
        data.occurrences =
          this.endCondition === 'occurrences'
            ? (this.occurrences ?? undefined)
            : undefined;
        data.endDate =
          this.endCondition === 'endDate' ? (this.endDate ?? undefined) : undefined;
      }
    }

    this.notificationService.DialogWindow<
      PEBLFormData,
      {
        status: boolean;
        resp?: PEBLPaymentResponse;
        scheduleResp?: ScheduleResponse;
        errors: string[];
      }
    >(
      PeblConfirmationDialogComponent,
      data,
      (resp: {
        status: boolean;
        resp?: PEBLPaymentResponse;
        scheduleResp?: ScheduleResponse;
        errors: string[];
      }) => {
        if (resp !== undefined && resp !== null && resp.status) {
          this.page = 'payment-success';
          this.linkResponse = resp.resp;
          this.scheduleResponse = resp.scheduleResp;
        }
      },
      {
        maxWidth: '500px',
      },
    );
  }

  public copyRespText(): void {
    if (!this.linkResponse) return;
    navigator.clipboard.writeText(this.linkResponse.token);
    this.notificationService.SmallDialog('Copied to clipboard', 'Close', 3000);
  }

  public ResetPage(): void {
    this.formsActive = false;
    this.linkResponse = undefined;
    this.scheduleResponse = undefined;
    this.scheduleEnabled = false;
    this.scheduleMode = 'once';
    this.sendAtDate = null;
    this.sendAtTime = '';
    this.frequency = 'weekly';
    this.startDate = null;
    this.startTime = '';
    this.endCondition = 'occurrences';
    this.occurrences = null;
    this.endDate = null;
    this.minScheduleDate = new Date();
    this.peblForm = this.BuildForm();
    this.peblSendForm = this.BuildSendForm();
    this.sendDetails = { sendAddress: '' };
    this.sendMethod = PeblSendType.NONE;
    this.formStage = PeblFormStage.PAYMENT_DETAILS;
    this.page = 'payment';
    this.formSubmit = new EventEmitter<void>();
    this.refreshFormState = new EventEmitter<void>();
    setTimeout(() => {
      this.formsActive = true;
    }, 100);
  }

  private BuildForm(): FormData<PEBLFormData> {
    const userProfile = this.merchantService.getUserprofile();
    const merchantName = this.merchantService.getCurrentBuidName();
    const currentMid = this.merchantService.getCurrentMid();

    const { amountFieldProperties, currencyFieldProperties } = paymentAmountFields({
      amountFieldName: 'amount',
      currencyFieldName: 'currencyCode',
      midCurrencies: currentMid.currencyCodes,
      countryConvertService: this.countryConvertService,
      currencyPipe: this.currencyPipe,
      onCurrencyChange: (iconType: InputIconType, symbol: string) => {
        if (this.peblForm) {
          const amountField = this.peblForm.formFields.find(f => f.id === 'amount');
          if (amountField && amountField.data && amountField.data.prefixIcon) {
            amountField.data.prefixIcon.iconType = iconType;
            amountField.data.prefixIcon.value = symbol;
          }
        }
      }
    });

    const formBuilder = new FormBuilder<PEBLFormData>(
      MonekColour.Primary,
      'Link Details',
    )
      .ToggleSubmitButton(false)
      //.SubmitButtonConfig('Confirm', Size.Full, Alignment.Left)
      .AddFormField(
        'OperatorId',
        '',
        InputType.Hidden,
        userProfile?.userId || '',
        Size.Full,
      )
      .AddFormField(
        'OperatorName',
        '',
        InputType.Hidden,
        userProfile?.name || '',
        Size.Full,
      )
      .AddFormField(
        'OperatorEmail',
        '',
        InputType.Hidden,
        userProfile?.email || '',
        Size.Full,
      )
      .AddFormField(
        'merchantName',
        '',
        InputType.Hidden,
        merchantName,
        Size.Full,
      )
      .AddFormField('merchantId', '', InputType.Hidden, '', Size.Full)
      .AddFormField('settle', '', InputType.Hidden, 'NOW', Size.Full);

    if (this.merchantService.getTestMidCondition() === TestMidCondition.Limited) {
      formBuilder.AddSection(
        '-- The currently selected MID cannot create a payment link, '
        + 'but you can still test this form --'
      );
    }

    const form = formBuilder.AddFormField(...amountFieldProperties)
      .AddOptionField(...currencyFieldProperties)
      .AddFormField(
        'paymentDetail',
        'Reference or Description',
        InputType.Text,
        null,
        Size.Full,
        new FormValidation()
          .MinLength(1, true, 'Detail must be at least 1 character')
          .MaxLength(60, true, 'Detail can not exceed 60 characters')
          .Required(),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'sell'),
        },
      )
      .AddFormField(
        'name',
        'Customer Name',
        InputType.Text,
        null,
        Size.Full,
        new FormValidation().Required().MaxLength(60),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'face'),
        },
      )
      .AddDetail(
        'Please choose how long you want this payment link to be valid',
      )
      .AddOptionField(
        `preparedPaymentMinutes`,
        'Expiry Time',
        OptionInputType.Radio,
        '1440',
        [
          new FormOption('60', '1 hour'),
          new FormOption('1440', '24 Hours'),
          new FormOption('10080', '1 Week'),
          new FormOption('43800', '1 Month'),
        ],
        Size.Full,
        new FormValidation().Required(),
        true,
      )
      .OnChange((data: PEBLFormData, valid: boolean) => {
        if (valid) {
          this.formStage =
            this.sendMethod !== PeblSendType.NONE
              ? PeblFormStage.SENDER_INFO
              : PeblFormStage.SENDER_METHOD;
        } else {
          this.formStage = PeblFormStage.PAYMENT_DETAILS;
        }
      })
      .Build((data: PEBLFormData) => {
        this.SubmitForm(data);
      });

    return form;
  }

  private BuildSendForm(): FormData<{ sendAddress: string }> {
    let icon = new InputPrefixSuffixType(InputIconType.ICON, '');

    let validation = new FormValidation()
      .RequiredIf(!this.isLink(this.sendMethod))
      .PatternMatch(
        this.sendMethod === PeblSendType.EMAIL
          ? ValidationPattern.Email
          : this.sendMethod === PeblSendType.SMS
            ? ValidationPattern.UK_PHONE
            : ValidationPattern.AlphaNumeric,
      );

    if (
      this.sendMethod !== null &&
      this.sendMethod !== PeblSendType.NONE &&
      this.sendMethods !== undefined
    ) {
      const selectedSendMethod = this.sendMethods.filter(
        (sm) => sm.sendType == this.sendMethod,
      );
      if (selectedSendMethod.length !== 0) {
        icon = new InputPrefixSuffixType(
          InputIconType.ICON,
          selectedSendMethod[0].icon,
        );
      }

      if (this.sendMethod === PeblSendType.LINK)
        validation = new FormValidation();
      else if (this.sendMethod === PeblSendType.SMS)
        validation = validation.StartsWith(
          ['01', '02', '03', '07', '+441', '+442', '+443', '+447'],
          'Non valid UK Phone number format.',
        );
    }

    const form = new FormBuilder<{ sendAddress: string }>(
      MonekColour.Primary,
      'Send Details',
    )
      .ToggleSubmitButton(true)
      .SubmitButtonConfig('Confirm', Size.Half, Alignment.Center)
      .AddFormField(
        'sendAddress',
        this.customerDetailPlaceholder(this.sendMethod),
        InputType.Text,
        null,
        Size.Full,
        validation,
        false,
        {
          prefixIcon: icon,
        },
      )
      .OnChange((data: { sendAddress: string }, valid: boolean) => { })
      .Build((data: { sendAddress: string }) => {
        this.sendDetails = data;
        this.formSubmit.emit();
      });

    this.refreshFormState.emit();

    return form;
  }

  public SelectSendMethod(type: PeblSendType): void {
    if (this.scheduleEnabled && this.isLink(type)) return; // scheduled links must be sent by email or SMS
    this.sendMethod = type;
    this.peblSendForm = this.BuildSendForm();
    if (
      this.sendMethod !== PeblSendType.NONE &&
      this.formStage >= PeblFormStage.SENDER_METHOD
    ) {
      this.formStage = PeblFormStage.SENDER_INFO;
    }
  }

  public isLink(type: PeblSendType): boolean {
    return type == PeblSendType.LINK;
  }

  public OnScheduleToggle(): void {
    this.minScheduleDate = new Date();
    if (!this.scheduleEnabled) return;

    if (this.isLink(this.sendMethod)) {
      this.sendMethod = PeblSendType.NONE;
      this.formStage = PeblFormStage.SENDER_METHOD;
    }

    const defaultDateTime = new Date(Date.now() + 60 * 60000);
    const defaultTime = `${String(defaultDateTime.getHours()).padStart(2, '0')}:${String(
      defaultDateTime.getMinutes(),
    ).padStart(2, '0')}`;

    if (!this.sendAtDate) this.sendAtDate = defaultDateTime;
    if (!this.sendAtTime) this.sendAtTime = defaultTime;
    if (!this.startDate) this.startDate = defaultDateTime;
    if (!this.startTime) this.startTime = defaultTime;
  }

  public get maxEndDate(): Date | null {
    if (!this.startDate) return null;
    const max = new Date(this.startDate);
    max.setFullYear(max.getFullYear() + 2);
    return max;
  }

  private combineDateTime(date: Date | null, time: string): Date | undefined {
    if (!date) return undefined;
    const combined = new Date(date);
    const [hours, minutes] = (time || '00:00').split(':').map(Number);
    combined.setHours(hours || 0, minutes || 0, 0, 0);
    return combined;
  }

  public scheduleErrors(): string[] {
    const errors: string[] = [];
    if (!this.scheduleEnabled) return errors;

    // buffer over the backend's 5-minute minimum lead time
    const minSendTime = new Date(Date.now() + 6 * 60000);

    if (this.scheduleMode === 'once') {
      const sendAt = this.combineDateTime(this.sendAtDate, this.sendAtTime);
      if (!sendAt || !this.sendAtTime)
        errors.push('Please choose a send date and time');
      else if (sendAt < minSendTime)
        errors.push('The send time must be at least 5 minutes in the future');
    } else {
      const start = this.combineDateTime(this.startDate, this.startTime);
      if (!start || !this.startTime)
        errors.push('Please choose a start date and time');
      else if (start < minSendTime)
        errors.push('The start time must be at least 5 minutes in the future');

      if (this.endCondition === 'occurrences') {
        if (
          this.occurrences == null ||
          !Number.isInteger(this.occurrences) ||
          this.occurrences < 1 ||
          this.occurrences > 52
        )
          errors.push('Number of sends must be between 1 and 52');
      } else {
        if (!this.endDate) errors.push('Please choose an end date');
        else if (start) {
          if (this.endDate <= start)
            errors.push('The end date must be after the start date');
          else if (this.maxEndDate && this.endDate > this.maxEndDate)
            errors.push(
              'The end date can be no more than 2 years after the start date',
            );
        }
      }
    }

    return errors;
  }

  public scheduleValid(): boolean {
    return this.scheduleErrors().length === 0;
  }

  public customerDetailPlaceholder(type: PeblSendType): string {
    switch (type) {
      case PeblSendType.EMAIL:
        return 'Recipient Email';
      case PeblSendType.SMS:
        return 'Recipient Phone Number (+44 **** ******)';
      case PeblSendType.LINK:
        return 'Link will be generated when you click confirm';
    }

    return '';
  }
}

